import { useState, useEffect, useCallback } from 'react';
import { isNativePlatform } from '../services/platform';
import {
  checkBiometryAvailability,
  promptBiometric,
  storeBiometricToken,
  retrieveBiometricToken,
  clearBiometricToken,
  isBiometricEnrolled,
  getStoredBiometryType,
  storeBiometryType,
  getDeviceId
} from '../services/biometricService';
import api from '../services/api';
import { secureSet } from '../services/secureStorage';

export function useBiometricAuth() {
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [biometryType, setBiometryType] = useState(null);
  const [biometryAvailable, setBiometryAvailable] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [isPrompting, setIsPrompting] = useState(false);
  const [error, setError] = useState(null);

  // Check availability and enrollment status on mount
  useEffect(() => {
    if (!isNativePlatform()) {
      setIsChecking(false);
      return;
    }

    const check = async () => {
      const availability = await checkBiometryAvailability();
      setBiometryAvailable(availability.available);
      setBiometryType(availability.biometryType);

      const enrolled = await isBiometricEnrolled();
      setIsEnrolled(enrolled);

      if (enrolled) {
        const storedType = await getStoredBiometryType();
        if (storedType) setBiometryType(storedType);
      }

      // If biometry was removed from device but we have a stored token, clean up
      if (!availability.available && enrolled) {
        await clearBiometricToken();
        setIsEnrolled(false);
      }

      setIsChecking(false);
    };

    check();
  }, []);

  /**
   * Enroll biometric auth after a successful password login
   */
  const enrollBiometric = useCallback(async (currentToken) => {
    setError(null);

    // Verify biometry with user
    const promptResult = await promptBiometric('Confirm your identity to enable biometric sign-in');
    if (!promptResult.success) {
      if (promptResult.error !== 'USER_CANCELLED') {
        setError(promptResult.error);
      }
      return false;
    }

    try {
      const deviceId = await getDeviceId();
      if (!deviceId) {
        setError('Could not identify device');
        return false;
      }

      // Request biometric token from backend
      const response = await api.post('/api/auth/biometric-token', { deviceId }, {
        headers: { 'x-auth-token': currentToken }
      });

      // Store the biometric token
      await storeBiometricToken(response.data.biometricToken);

      // Store biometry type for UI
      const availability = await checkBiometryAvailability();
      if (availability.biometryType) {
        await storeBiometryType(availability.biometryType);
        setBiometryType(availability.biometryType);
      }

      setIsEnrolled(true);
      return true;
    } catch (err) {
      console.error('Biometric enrollment failed:', err);
      setError('Failed to enable biometric sign-in');
      return false;
    }
  }, []);

  /**
   * Attempt biometric unlock - returns { token, user } on success
   */
  const attemptBiometricUnlock = useCallback(async () => {
    setError(null);
    setIsPrompting(true);

    try {
      const promptResult = await promptBiometric('Sign in to Finance Forecaster');
      if (!promptResult.success) {
        setIsPrompting(false);
        if (promptResult.error !== 'USER_CANCELLED') {
          setError(promptResult.error);
        }
        return null;
      }

      const biometricToken = await retrieveBiometricToken();
      if (!biometricToken) {
        setIsPrompting(false);
        setError('No biometric token found');
        setIsEnrolled(false);
        return null;
      }

      // Exchange biometric token for session JWT
      const response = await api.post('/api/auth/biometric-refresh', { biometricToken });

      // Store new session token
      await secureSet('token', response.data.token);

      setIsPrompting(false);
      return { token: response.data.token, user: response.data.user };
    } catch (err) {
      setIsPrompting(false);
      const errorCode = err.response?.data?.error;

      if (errorCode === 'BIOMETRIC_TOKEN_EXPIRED' || errorCode === 'BIOMETRIC_REVOKED') {
        await clearBiometricToken();
        setIsEnrolled(false);
        setError('Biometric sign-in expired. Please sign in with your password.');
      } else {
        setError('Biometric sign-in failed');
      }
      return null;
    }
  }, []);

  /**
   * Revoke biometric auth
   */
  const revokeBiometric = useCallback(async () => {
    try {
      const deviceId = await getDeviceId();
      await api.delete('/api/auth/biometric-token', { data: { deviceId } });
    } catch (err) {
      // Still clear locally even if server call fails
      console.error('Server revoke failed:', err);
    }

    await clearBiometricToken();
    setIsEnrolled(false);
  }, []);

  return {
    isEnrolled,
    biometryType,
    biometryAvailable,
    isChecking,
    isPrompting,
    error,
    enrollBiometric,
    attemptBiometricUnlock,
    revokeBiometric
  };
}
